import React from 'react';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { effectEnvelope, effectProgress } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { TypedMemo } from '../../../common/types';
import { bell, effectColour, flashShape, hash01, intensityOf, textParam } from '../previewEffectsMath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for these parts of the editor; each part uses its own slice of it */
import styles from './PreviewEffectSketches.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SketchProps {
  effect: MusicVideoEffect;
  sec: number;
  accent: string;
}

const GLITCH_COLOURS = ['#00f0ff', '#ff2bd6'] as const;

/**
 * The part of a transition over the footage (the sketch of `TransitionEffects.tsx` of the render):
 * the flash, the film burn light, the glitch blocks and scan lines, the speed streaks of a whip, the
 * bloom of a zoom. The move of the footage itself is in `previewFootageMotion`.
 */
const TransitionSketchComponent: React.FC<SketchProps> = ({ effect, sec, accent }) => {
  const progress = effectProgress(effect, sec);
  if (progress === null) {
    return null;
  }
  const intensity = intensityOf(effect);
  const peak = bell(progress);
  switch (effect.kind) {
    case 'flashCut':
      return (
        <div
          className={styles.fill}
          style={{
            background: effectColour(effect, accent, 'white'),
            opacity: flashShape(progress) * (0.55 + 0.45 * intensity),
          }}
        />
      );
    case 'filmBurn': {
      const x = 10 + progress * 80;
      const warm = effectColour(effect, accent, 'accent');
      return (
        <>
          <div
            className={styles.screen}
            style={{
              opacity: peak * (0.5 + 0.5 * intensity),
              background: `radial-gradient(40% 70% at ${x}% 45%, rgb(255 250 235 / 90%), transparent 60%), radial-gradient(70% 90% at ${x - 12}% 60%, rgb(255 120 40 / 75%), transparent 70%), radial-gradient(60% 80% at ${x + 18}% 30%, ${warm}, transparent 70%)`,
            }}
          />
          <div className={styles.burnEdges} style={{ opacity: peak * intensity }} />
        </>
      );
    }
    case 'glitch': {
      const envelope = effectEnvelope(effect, sec, 0.08);
      const step = Math.floor(sec * 15);
      const count = 3 + Math.floor(hash01(step + 11) * 4);
      return (
        <div className={styles.fill} style={{ opacity: envelope }}>
          {Array.from({ length: count }, (_, index) => {
            const seed = step * 13 + index * 7;
            const colour = index === 0 ? accent : GLITCH_COLOURS[index % 2];
            return (
              <span
                key={index}
                className={styles.glitchBlock}
                style={{
                  left: `${hash01(seed) * 80}%`,
                  top: `${hash01(seed + 1) * 90}%`,
                  width: `${8 + hash01(seed + 2) * 30 * (0.5 + intensity)}%`,
                  height: `${1 + hash01(seed + 3) * 6}%`,
                  background: colour,
                }}
              />
            );
          })}
          <div className={styles.scanLines} style={{ opacity: 0.25 + 0.35 * intensity }} />
        </div>
      );
    }
    case 'whip': {
      const direction = textParam(effect, 'direction', 'left');
      const horizontal = direction === 'left' || direction === 'right';
      return (
        <div
          className={horizontal ? styles.streaksX : styles.streaksY}
          style={{ opacity: peak * peak * (0.35 + 0.5 * intensity) }}
        />
      );
    }
    case 'zoomThrough':
      return (
        <>
          <div className={styles.bloom} style={{ opacity: peak * peak * intensity * 0.7 }} />
          <div className={styles.vignette} style={{ opacity: peak * (0.4 + 0.4 * intensity) }} />
        </>
      );
    case 'lyricKinetic':
    case 'lyricHook':
    case 'titleCard':
    case 'artistCard':
    case 'chapterCard':
    case 'splitScreen':
    case 'freezeStroke':
    case 'rgbSplit':
    case 'beatShake':
    case 'colourPulse':
    default:
      return null;
  }
};

export const TransitionSketch = TypedMemo(TransitionSketchComponent);
